/**
 * One search box, four range chips, one Filter button.
 *
 * The box is the whole point of this screen: an order number, a name, an email, a phone in
 * any format, a ZIP, a tracking number or the name on a Zelle payment, and the row comes
 * back. It writes to the URL through the page, debounced, so every keystroke is not a
 * request and the address bar is still a link when the typing stops.
 *
 * The auto-widen notice is the server's, not this component's. A search with no hits in a
 * bounded window is re-run against all time inside the same request, and `widened` says so.
 * This renders that sentence and offers the way back.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { OrderRange, OrdersPageWidened, PaymentMethod } from '@elutide/shared';
import { Button, Chip, SearchIcon } from '@/components';
import { isTypingTarget } from '@/lib/hooks';
import { PAYMENT_FILTERS, RANGE_WORDS, STATUS_FILTERS, STATUS_LABEL } from './types';
import { activeFilterCount, toggleInList, type OrdersSearch } from './searchParams';

const DEBOUNCE_MS = 150;

export interface OrdersToolbarProps {
  search: OrdersSearch;
  /** `replace` keeps a debounced keystroke out of the history stack. */
  onSearch: (next: OrdersSearch, options?: { replace?: boolean }) => void;
  /** Set when the server widened the window for this query, else null. */
  widened: OrdersPageWidened | null;
  /** How many rows the widened search found, for the sentence. */
  widenedCount: number;
  busy: boolean;
}

export function OrdersToolbar({
  search,
  onSearch,
  widened,
  widenedCount,
  busy,
}: OrdersToolbarProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState(search.q);
  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * The box follows the URL when the URL moves without it: Back, a KPI tile, a deep link.
   * It does not follow its own writes, because the debounce means the URL is always a
   * beat behind what has been typed and re-seeding from it would eat characters.
   */
  const pushed = useRef(search.q);
  useEffect(() => {
    if (search.q !== pushed.current) {
      pushed.current = search.q;
      setText(search.q);
    }
  }, [search.q]);

  useEffect(() => {
    if (text === search.q) return;
    const id = window.setTimeout(() => {
      pushed.current = text;
      onSearch({ ...search, q: text }, { replace: true });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [onSearch, search, text]);

  /**
   * Focus the box.
   *
   * Spec 02-orders makes this screen search first, so on this screen the search shortcut
   * lands here rather than on the global palette. Capture phase, because the shell's own
   * handler is bound on the document too and the last one added would otherwise win.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const wantsSearch =
        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') ||
        (event.key === '/' && !event.ctrlKey && !event.metaKey && !isTypingTarget(event.target));
      if (!wantsSearch) return;
      event.preventDefault();
      event.stopPropagation();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const setRange = useCallback(
    (range: OrderRange) => onSearch({ ...search, range }),
    [onSearch, search],
  );

  const clear = useCallback(() => {
    pushed.current = '';
    setText('');
    onSearch({ ...search, q: '' }, { replace: true });
    inputRef.current?.focus();
  }, [onSearch, search]);

  const filterCount = activeFilterCount(search);

  return (
    <div className="ord-toolbar">
      <div className="ord-toolbar__row">
        <div className="ord-search">
          <label className="o-visually-hidden" htmlFor={inputId}>
            Search orders
          </label>
          <SearchIcon />
          <input
            id={inputId}
            ref={inputRef}
            className="o-input ord-search__input"
            type="search"
            autoComplete="off"
            spellCheck={false}
            placeholder="Order #, name, email, phone, ZIP, tracking, payer"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && text !== '') {
                event.stopPropagation();
                clear();
              }
            }}
          />
          {text === '' ? null : (
            <Button variant="ghost" size="sm" onClick={clear} aria-label="Clear the search">
              Clear
            </Button>
          )}
          <span className="ord-search__state" aria-live="polite">
            {busy ? 'Searching' : ''}
          </span>
        </div>

        <div className="ord-ranges" role="group" aria-label="Placed within">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <Chip
              key={range}
              label={range === 'all' ? 'All' : range}
              active={search.range === range}
              onClick={() => setRange(range)}
              title={`Orders placed in ${RANGE_WORDS[range]}`}
            />
          ))}
        </div>

        <div className="ord-filter">
          <Button
            size="sm"
            variant={filterCount > 0 ? 'primary' : 'default'}
            aria-expanded={filtersOpen}
            aria-haspopup="dialog"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            Filter{filterCount > 0 ? ` (${filterCount})` : ''}
          </Button>
          {filtersOpen ? (
            <FilterPopover
              search={search}
              onSearch={onSearch}
              onClose={() => setFiltersOpen(false)}
            />
          ) : null}
        </div>
      </div>

      {widened ? (
        <p className="ord-widened" role="status">
          <span>
            No matches in {RANGE_WORDS[widened.from]}. Showing {widenedCount} from all time.
          </span>
          <Chip
            label={`Back to ${widened.from === 'all' ? 'the window' : widened.from}`}
            onClick={() => setRange(widened.from)}
          />
        </p>
      ) : null}
    </div>
  );
}

/**
 * The long tail, behind one button.
 *
 * Status, payment method and first order only. Everything else the spec asks for is on the
 * rail or the range chips, and a filter panel that repeats them is a second place to set
 * the same thing to a different value.
 */
function FilterPopover({
  search,
  onSearch,
  onClose,
}: {
  search: OrdersSearch;
  onSearch: (next: OrdersSearch, options?: { replace?: boolean }) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    // Capture phase, so Escape closes this popover and does not travel on to anything
    // else listening on the document.
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  return (
    <div className="ord-pop" role="dialog" aria-label="Filters" ref={ref}>
      <fieldset className="ord-pop__group">
        <legend className="o-field__label">Status</legend>
        <div className="ord-pop__chips">
          {STATUS_FILTERS.map((status) => (
            <Chip
              key={status.id}
              label={status.label}
              active={search.status.includes(status.id)}
              onClick={() =>
                onSearch({ ...search, status: toggleInList(search.status, status.id) })
              }
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="ord-pop__group">
        <legend className="o-field__label">Payment method</legend>
        <div className="ord-pop__chips">
          {PAYMENT_FILTERS.map((method) => (
            <Chip
              key={method.id}
              label={method.label}
              active={search.payment_method.includes(method.id)}
              onClick={() =>
                onSearch({
                  ...search,
                  payment_method: toggleInList<PaymentMethod>(search.payment_method, method.id),
                })
              }
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="ord-pop__group">
        <legend className="o-field__label">Customer</legend>
        <div className="ord-pop__chips">
          <Chip
            label="First order only"
            active={search.first_order}
            onClick={() => onSearch({ ...search, first_order: !search.first_order })}
          />
        </div>
      </fieldset>
    </div>
  );
}

/**
 * The active filters, spelled out and removable.
 *
 * A filter you cannot see is a filter that makes the page lie about how many orders exist.
 * Every one that is on renders here with the click that takes it off again.
 */
export function ActiveFilterChips({
  search,
  onSearch,
}: {
  search: OrdersSearch;
  onSearch: (next: OrdersSearch, options?: { replace?: boolean }) => void;
}) {
  if (activeFilterCount(search) === 0) return null;

  return (
    <div className="ord-active" role="group" aria-label="Active filters">
      {search.status.map((status) => (
        <Chip
          key={`status-${status}`}
          label={`${STATUS_LABEL[status] ?? status} x`}
          active
          onClick={() => onSearch({ ...search, status: toggleInList(search.status, status) })}
          title="Remove this filter"
        />
      ))}
      {search.payment_method.map((method) => (
        <Chip
          key={`method-${method}`}
          label={`${PAYMENT_FILTERS.find((m) => m.id === method)?.label ?? method} x`}
          active
          onClick={() =>
            onSearch({
              ...search,
              payment_method: toggleInList<PaymentMethod>(search.payment_method, method),
            })
          }
          title="Remove this filter"
        />
      ))}
      {search.first_order ? (
        <Chip
          label="First order only x"
          active
          onClick={() => onSearch({ ...search, first_order: false })}
          title="Remove this filter"
        />
      ) : null}
    </div>
  );
}
